import { Injectable } from '@angular/core';
import { FormActionHandlerService } from './base.service';
import { FormStatusService } from '../status.service';
import { SwalService } from '../../../fastlane-common/services/swal.service';
import { FormDataService } from '../data.service';
import { FormShareService } from '../share.service';
import { EmailService } from '../../../email/services/email.service';
import { formStatus } from 'app/form/form.constants';
import { SweetAlertOptions } from 'sweetalert2';


/**
 * R&D: the manager's adjusted Louisiana Research Credit raised the fee above what the applicant
 * has paid. Replaces "Approve Form (Manager)": the manager enters a reason, the applicant is
 * emailed the payment request, and the form goes back to the applicant as Pending Payment.
 */
@Injectable()
export class RdReturnForPaymentActionHandlerService extends FormActionHandlerService {
  constructor(
    private dataService: FormDataService,
    private statusService: FormStatusService,
    private swal: SwalService,
    private shareService: FormShareService,
    private emailService: EmailService
  ) {
    super();
  }

  exec(): Promise<any> {
    const that = this;

    const swalOptions = <SweetAlertOptions>{
      title: 'Return this form to the applicant for payment?',
      inputPlaceholder:
        'Enter the reason an additional fee is required. This will be emailed to the applicant and saved to the form\'s status history.',
      type: 'question',
      confirmButtonText: 'Yes, Return For Payment',
      allowOutsideClick: false
    };
    const emptyReasonMessage =
      'A reason must be entered in order to return the form for payment.';

    return new Promise((resolve, reject) => {
      that.swal
        .textarea(swalOptions, 'required', emptyReasonMessage)
        .then(reason => {
          that.swal.load('Returning form to the applicant for payment...');

          that.statusService.changeStatus(formStatus.pendingPayment);
          const pendingPaymentEntry = that.shareService.form.statuses[0];
          if (pendingPaymentEntry) {
            pendingPaymentEntry.comment = reason;
          }

          that.dataService.saveForm().subscribe(
            () => {
              that.notifyApplicant(reason).then(emailSent => {
                that.swal
                  .success({
                    title: 'Form Returned For Payment',
                    text: emailSent
                      ? 'The form is now Pending Payment and the applicant has been emailed the payment request.'
                      : 'The form is now Pending Payment, but the email to the applicant could not be sent. ' +
                        'Please contact the applicant directly.'
                  })
                  .then(() => resolve())
                  .catch(() => resolve());
              });
            },
            errorSaving => {
              if (errorSaving === 409) {
                reject(errorSaving);
              } else {
                reject(
                  'We were unable to return the form for payment. Please try again later.'
                );
              }
            }
          );
        })
        .catch(() => resolve());
    });
  }

  // Best-effort: a failed email never undoes the saved status change.
  private notifyApplicant(reason: string): Promise<boolean> {
    const that = this;
    return new Promise(resolve => {
      const project = that.shareService.project;
      that.emailService
        .sendRdAdditionalPaymentRequestedEmail(
          that.shareService.projectBase.projectInfo,
          that.shareService.form,
          that.shareService.formId.projectGuid,
          project ? project.projectContacts : [],
          reason
        )
        .subscribe(() => resolve(true), () => resolve(false));
    });
  }
}
